/**
 * 🚨 500 ERROR PAGE
 * Professional "Internal Server Error" component
 * Handles server-side errors with user-friendly messaging
 */
import React from 'react';
import { vars } from '../../../../styles/vars.css';
import { Link, useNavigate } from 'react-router-dom';
import * as styles from './errorPages.css';

interface ServerErrorPageProps {
  /** Optional custom message */
  message?: string;
  /** Error details for technical users */
  errorDetails?: {
    code?: string;
    timestamp?: string;
    requestId?: string;
  };
  /** Show retry button */
  showRetry?: boolean;
  /** Auto-retry interval in seconds */
  autoRetryInterval?: number;
}

const ServerErrorPage: React.FC<ServerErrorPageProps> = ({
  message,
  errorDetails,
  showRetry = true,
  autoRetryInterval
}) => {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const [isRetrying, setIsRetrying] = React.useState(false);
  const [countdown, setCountdown] = React.useState(autoRetryInterval || 0);

  // Auto-retry countdown
  React.useEffect(() => {
    if (autoRetryInterval && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (autoRetryInterval && countdown === 0 && retryCount < 3) {
      handleRetry();
    }
  }, [countdown, autoRetryInterval, retryCount]);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    
    // Simulate retry delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Reload the current page
    window.location.reload();
  };

  const suggestions = [
    'Rechargez la page dans quelques instants',
    'Vérifiez votre connexion internet',
    'Essayez d\'accéder à une autre section du site',
    'Contactez le support si le problème persiste'
  ];

  return (
    <div className={styles.errorContainer}>
      {/* Breadcrumb for context */}
      <nav className={styles.breadcrumb}>
        <Link to="/" className={styles.breadcrumbLink}>Accueil</Link>
        <span> / </span>
        <span>Erreur 500</span>
      </nav>

      <div className={styles.errorContent}>
        {/* Error Icon */}
        <div className={styles.errorIcon}>
          ⚠️
        </div>

        {/* Error Code */}
        <h1 className={styles.errorCode}>
          500
          <span className={styles.srOnly}>Erreur cinq cents</span>
        </h1>

        {/* Error Title */}
        <h2 className={styles.errorTitle}>
          Erreur Interne du Serveur
        </h2>

        {/* Error description */}
        <p className={styles.errorDescription}>
          {message || 
            "Une erreur inattendue s'est produite sur nos serveurs. Nos équipes techniques ont été automatiquement notifiées et travaillent à résoudre le problème."
          }
        </p>

        {/* Auto-retry countdown */}
        {autoRetryInterval && countdown > 0 && (
          <div style={{
            padding: '1rem',
            backgroundColor: 'var(--colors-info-bg)',
            borderRadius: '8px',
            border: '1px solid var(--colors-info-border)',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <strong>Nouvelle tentative automatique dans {countdown} seconde{countdown > 1 ? 's' : ''}</strong>
            <br />
            <small>Tentative {retryCount + 1}/3</small>
          </div>
        )}

        {/* Suggestions */}
        <div>
          <h3 style={{ 
            fontSize: '1.1rem', 
            marginBottom: '1rem', 
            color: 'var(--colors-text)',
            fontWeight: 600 
          }}>
            Que pouvez-vous essayer ?
          </h3>
          <ul className={styles.suggestionsList}>
            {suggestions.map((suggestion, index) => (
              <li key={index} className={styles.suggestionItem}>
                {suggestion}
              </li>
            ))}
          </ul>
        </div>

        {/* action Buttons */}
        <div className={styles.actionButtons}>
          {showRetry && (
            <button 
              onClick={handleRetry}
              disabled={isRetrying}
              className={styles.primaryButton}
              style={{
                opacity: isRetrying ? 0.6 : 1,
                cursor: isRetrying ? 'not-allowed' : 'pointer'
              }}
            >
              <span>{isRetrying ? '🔄' : '🔄'}</span>
              {isRetrying ? 'Rechargement...' : 'Réessayer'}
            </button>
          )}
          <Link to="/" className={styles.secondaryButton}>
            <span>🏠</span>
            Retour à l'accueil
          </Link>
          <button 
            onClick={handleGoBack}
            className={styles.secondaryButton}
          >
            <span>⬅️</span>
            Page précédente
          </button>
        </div>

        {/* Status Indicator */}
        <div className={styles.statusIndicator}>
          <div className={styles.statusDot} style={{ backgroundColor: vars.color.danger }}></div>
          <span>Incident détecté - Investigation en cours</span>
        </div>

        {/* Technical Details */}
        <div className={styles.technicalDetails}>
          <h4 
            className={styles.technicalTitle}
            onClick={() => setShowDetails(!showDetails)}
          >
            <span>{showDetails ? '🔽' : '▶️'}</span>
            Détails techniques
          </h4>
          {showDetails && (
            <div className={styles.technicalContent}>
              {`Erreur: 500 Internal Server Error
Timestamp: ${errorDetails?.timestamp || new Date().toISOString()}
Code d'erreur: ${errorDetails?.code || 'ERR_SERVER_FAULT'}
Request ID: ${errorDetails?.requestId || Math.random().toString(36).substr(2, 9)}
Tentatives: ${retryCount}
User Agent: ${navigator.userAgent}
URL: ${window.location.href}`}
            </div>
          )}
        </div>

        {/* Help section */}
        <div className={styles.helpSection}>
          <h3 className={styles.helpTitle}>
            Le problème persiste ?
          </h3>
          <p className={styles.helpText}>
            Si cette erreur continue d'apparaître, merci de nous contacter en incluant 
            l'ID de la requête affiché ci-dessus.
          </p>
          <div className={styles.contactInfo}>
            <a href="mailto:jf.c.webcraft@gmail.com" className={styles.contactLink}>
              📧 jf.c.webcraft@gmail.com
            </a>
            <span style={{ fontSize: '0.9rem', color: 'var(--colors-text-secondary)' }}>
              🔍 Incident automatiquement rapporté (ID: {errorDetails?.requestId || 'AUTO'})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServerErrorPage;
